import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { preloadRoute } from '../utils/routePreloader';
import { CATEGORIES, getSubcategorySlug } from '../data/categories';

const NAV_ITEMS = [
  { to: '/shop', label: 'shop all', end: true },
  ...CATEGORIES.map((category) => ({
    to: `/${category.slug}`,
    label: category.label.toLowerCase(),
    subcategories: category.subcategories.map((sub) => ({
      label: sub.toLowerCase(),
      to: `/${category.slug}/${getSubcategorySlug(sub)}`,
    })),
  })),
  { to: '/about', label: 'about' },
];

export default function Navbar() {
  const { cartItems } = useCart();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openSlug, setOpenSlug] = useState(null);
  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const openItem = NAV_ITEMS.find((item) => item.to === openSlug && item.subcategories?.length);

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(min-width: 641px)');
    const handleChange = (event) => {
      if (event.matches) {
        setMenuOpen(false);
      }
    };

    if (mediaQuery.matches) {
      setMenuOpen(false);
    }

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const rootStyle = document.documentElement.style;
    const syncHeaderMetrics = () => {
      const height = headerRef.current?.offsetHeight;
      if (!height) return;

      rootStyle.setProperty('--mobile-header-height', `${height}px`);
      rootStyle.setProperty('--mobile-header-offset', `${height + 14}px`);
    };

    syncHeaderMetrics();

    let resizeObserver;
    if ('ResizeObserver' in window && headerRef.current) {
      resizeObserver = new ResizeObserver(syncHeaderMetrics);
      resizeObserver.observe(headerRef.current);
    }

    window.addEventListener('resize', syncHeaderMetrics);

    return () => {
      window.removeEventListener('resize', syncHeaderMetrics);
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!menuOpen || typeof document === 'undefined' || typeof window === 'undefined') return undefined;

    const scrollY = window.scrollY;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyPosition = document.body.style.position;
    const previousBodyTop = document.body.style.top;
    const previousBodyWidth = document.body.style.width;
    const previousBodyLeft = document.body.style.left;
    const previousBodyRight = document.body.style.right;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.position = previousBodyPosition;
      document.body.style.top = previousBodyTop;
      document.body.style.width = previousBodyWidth;
      document.body.style.left = previousBodyLeft;
      document.body.style.right = previousBodyRight;
      window.scrollTo(0, scrollY);
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setOpenSlug(null);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setOpenSlug(null);
      if (menuOpen) {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  return (
    <>
      <header className="header" ref={headerRef}>
        <div className="header-inner" onMouseLeave={() => setOpenSlug(null)}>
          <Link to="/" className="header-logo" onClick={closeMenu}>
            <span className="header-logo-text">Vettail</span>
          </Link>

          <nav className="header-nav" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `header-nav-link${isActive ? ' active' : ''}${openSlug === item.to && item.subcategories?.length ? ' header-nav-link--open' : ''}`
                }
                onMouseEnter={() => {
                  preloadRoute(item.to);
                  setOpenSlug(item.subcategories?.length ? item.to : null);
                }}
                onFocus={() => {
                  preloadRoute(item.to);
                  setOpenSlug(item.subcategories?.length ? item.to : null);
                }}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {openItem && (
            <div className="mega-menu">
              <nav className="mega-menu-inner" aria-label={`${openItem.label} subcategories`}>
                <Link to={openItem.to} className="mega-menu-link mega-menu-link--all">
                  all {openItem.label}
                </Link>
                {openItem.subcategories.map((sub) => (
                  <Link key={sub.to} to={sub.to} className="mega-menu-link">
                    {sub.label}
                  </Link>
                ))}
              </nav>
            </div>
          )}

          <div className="header-actions">
            <Link to="/cart" className="icon-btn cart-icon-btn" aria-label={`Cart${itemCount > 0 ? `, ${itemCount} items` : ''}`} onClick={closeMenu}>
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
            </Link>

            <button
              type="button"
              className="icon-btn icon-btn--button mobile-menu-btn"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav-drawer"
              onClick={() => setMenuOpen(v => !v)}
              ref={menuButtonRef}
            >
              {menuOpen ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className={`mobile-nav-layer${menuOpen ? ' mobile-nav-layer--open' : ''}`} aria-hidden={!menuOpen}>
        <button type="button" className="mobile-nav-backdrop" aria-label="Close menu" onClick={closeMenu} />
        <div
          id="mobile-nav-drawer"
          className={`mobile-nav-drawer${menuOpen ? ' mobile-nav-drawer--open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
        >
          <div className="mobile-nav-sheet">
            <section className="mobile-nav-section" aria-label="shop">
              <p className="mobile-nav-eyebrow">shop</p>
              <div className="mobile-nav-section-links">
                {NAV_ITEMS.map((item) => (
                  <div key={item.to} className="mobile-nav-item-wrap">
                    <Link
                      to={item.to}
                      className={`mobile-nav-link${location.pathname === item.to ? ' mobile-nav-link--active' : ''}`}
                      onClick={closeMenu}
                    >
                      <span className="mobile-nav-link-label">{item.label}</span>
                      <span className="mobile-nav-link-arrow" aria-hidden="true">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path d="M4 12L12 4" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                          <path d="M6 4H12V10" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </Link>
                    {item.subcategories?.length > 0 && (
                      <div className="mobile-nav-sub-links">
                        {item.subcategories.map((sub) => (
                          <Link key={sub.to} to={sub.to} className="mobile-nav-sub-link" onClick={closeMenu}>
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <div className="mobile-nav-footer">vet-trusted pet essentials</div>
          </div>
        </div>
      </div>
    </>
  );
}
